import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Trash2, Info, MoreHorizontal, Sparkles } from 'lucide-react';

const WidgetHeader = ({
  title,
  subtitle,
  status,
  category,
  isEditing,
  isHovering,
  onEdit,
  onDelete,
  tempTitle,
  setTempTitle,
  showInfoButton,
  onInfoClick,
  promptContext,
  isLoadingAudit,
  onSmartAssist
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isHovering && !menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isHovering, menuOpen]);

  useEffect(() => {
    if (!isHovering) setMenuOpen(false);
  }, [isHovering]);

  const getCategoryColor = (category) => {
    if (!category) return '';
    const lowerCategory = category.toLowerCase();
    if (lowerCategory === 'immediate attention') return '#ef4444';
    if (lowerCategory === 'call preparation') return '#10b981';
    if (lowerCategory === 'portfolio review' || lowerCategory === 'portfolio analysis') return '#3b82f6';
    return '';
  };

  return (
    <>
      {(category) && (
        <div className="flex items-center gap-2.5 mb-2">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: getCategoryColor(category) }}
          ></div>
          <span
            className="font-medium text-xs uppercase tracking-wider"
            style={{ color: getCategoryColor(category) }}
          >
            {category ? category.toUpperCase() : ''}
          </span>
        </div>
      )}

      <div className="flex justify-between items-center mb-2">
        <div>
          {isEditing ? (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                Widget Title
              </label>
              <input
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                type="text"
                value={tempTitle}
                onChange={e => setTempTitle(e.target.value)}
                placeholder="Widget title"
              />
            </div>
          ) : (
            <>
              <div className="flex items-centers">
                <div className="text-lg font-semibold text-primary">
                  {title}
                </div>
                {status && (
                  <span
                    className="mx-3 bg-warning-dark text-white font-bold text-xs py-0.5 px-2.5 rounded-full"
                    style={{
                      backgroundColor: status.toLowerCase() === 'urgent' ? '#ef4444' :
                        status.toLowerCase() === 'new' ? '#10b981' : '#3b82f6'
                    }}
                  >
                    {status}
                  </span>
                )}
              </div>
              <div className="text-gray-500 text-sm">
                {subtitle}
              </div>
            </>
          )}
        </div>

        {!isEditing && (isHovering || isLoadingAudit) && (
          <div className="flex gap-2 items-center">
            {promptContext && isHovering && (
              <div className="text-xs bg-blue-50 text-primaryDark px-2 py-1 rounded-md mt-1 flex items-center">
                <Info size={12} className="mr-1" />
                Using context from other widgets
              </div>
            )}
            <div className="relative" ref={menuRef}>
              {isLoadingAudit ? (
                <div className="w-7 h-7 flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full border-2 border-t-primary border-primaryLight animate-spin" />
                </div>
              ) : (
                <>
                  <button
                    className="bg-transparent border-none cursor-pointer rounded flex items-center justify-center transition-colors hover:bg-gray-100 p-1"
                    onClick={() => setMenuOpen(prev => !prev)}
                    title="More actions"
                    type="button"
                  >
                    <MoreHorizontal size={17} className="text-gray-500" />
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 top-8 z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px]">
                      <button
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => { setMenuOpen(false); onSmartAssist(); }}
                        type="button"
                      >
                        <Sparkles size={14} className="text-secondary" />
                        Smart Assist
                      </button>
                      {showInfoButton && (
                        <button
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => { setMenuOpen(false); onInfoClick(); }}
                          type="button"
                        >
                          <Info size={14} className="text-secondary" />
                          Audit
                        </button>
                      )}
                      <button
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => { setMenuOpen(false); onEdit(); }}
                        type="button"
                      >
                        <Pencil size={14} className="text-secondary" />
                        Edit
                      </button>
                      <button
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                        onClick={() => { setMenuOpen(false); onDelete(); }}
                        type="button"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      </div >
    </>
  );
};

export default WidgetHeader;